import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { useHelpStore } from '@/lib/help/store';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// Check if a link is an internal documentation link
function isInternalDocLink(href: string | undefined): boolean {
  if (!href) return false;
  // Internal doc links start with / but are not external URLs
  return href.startsWith('/') && !href.startsWith('//');
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const navigateToDoc = useHelpStore((s) => s.navigateToDoc);
  return (
    <div className={cn('prose prose-sm dark:prose-invert max-w-none', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ children }) => (
            <h1 className="text-xl font-bold mt-6 mb-4 first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-lg font-semibold mt-5 mb-3 border-b pb-2">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-base font-semibold mt-4 mb-2">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-sm font-semibold mt-3 mb-2">{children}</h4>
          ),

          // Paragraphs
          p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,

          // Lists
          ul: ({ children }) => (
            <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2 ml-4 list-decimal space-y-1">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,

          // Code
          code: ({ className, children, ...props }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code
                  className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                className={cn(
                  'block p-3 rounded-lg bg-muted text-sm font-mono overflow-x-auto',
                  className
                )}
                {...props}
              >
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="my-3 rounded-lg bg-muted overflow-hidden">{children}</pre>
          ),

          // Links - handle internal doc links specially
          a: ({ href, children }) => {
            const isExternal = href?.startsWith('http');
            const isInternalDoc = isInternalDocLink(href);

            if (isInternalDoc) {
              // Internal documentation link - navigate within help panel with history
              return (
                <button
                  type="button"
                  className="text-primary hover:underline cursor-pointer inline text-left"
                  onClick={(e) => {
                    e.preventDefault();
                    if (href) {
                      navigateToDoc(href);
                    }
                  }}
                >
                  {children}
                </button>
              );
            }

            // External or other links - normal behavior
            return (
              <a
                href={href}
                className="text-primary hover:underline"
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
              >
                {children}
              </a>
            );
          },

          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/30 pl-4 my-3 italic text-muted-foreground">
              {children}
            </blockquote>
          ),

          // Tables
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-muted/50">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="border border-border px-3 py-2 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-border px-3 py-2">{children}</td>
          ),

          // Horizontal rule
          hr: () => <hr className="my-4 border-border" />,

          // Strong and emphasis
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
